/**
 * Authentication Guard (Functional)
 *
 * Protects routes that require a provisioned tablet. Only a device token
 * (from QR enrollment or interactive MSAL registration) grants entry — an
 * MSAL account alone is never sufficient. An unenrolled tablet must never
 * reach the shared dashboard, regardless of who signs in on it.
 *
 * A locally-present token is not enough on its own — a supervisor may have
 * revoked it server-side (e.g. a lost/decommissioned tablet), so entry also
 * requires a live GET /devices/status check. On any failure (revoked,
 * unrecognized, or unreachable) the local token is cleared and the tablet is
 * sent back to the unenrolled screen.
 */
import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';

import { environment } from '../../../environments/environment';
import { DeviceTokenService } from '../../shell/services/device-token.service';

export const authGuard: CanActivateFn = (_route, _state): Observable<boolean | UrlTree> => {
  const deviceToken = inject(DeviceTokenService);
  const http = inject(HttpClient);
  const router = inject(Router);

  const token = deviceToken.token();
  if (!token) {
    return of(router.parseUrl('/device-not-enrolled'));
  }

  return http.get(`${environment.apiUrl}/devices/status`, { headers: { 'X-Device-Token': token } }).pipe(
    map(() => true as boolean | UrlTree),
    catchError(() => {
      deviceToken.clearToken();
      return of(router.parseUrl('/device-not-enrolled'));
    }),
  );
};